/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import {
  ComponentProps,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { DiscographyId, SourceId } from '../useDiscographyIds';
import { Plus } from 'lucide-react';
import { SourceIdResolver } from './CertaintyMarker';
import { generateArtistUrlBySource } from './utils';

type IdTooltip = {
  id: string;
  position: {
    top: number;
    left: number;
  };
};

function Tooltip({ idTooltip }: { idTooltip: IdTooltip | null }) {
  if (idTooltip) {
    return (
      <div
        className="tw-fixed tw-bg-gray-900 tw-text-white tw-text-xs tw-py-1 tw-px-2 tw-rounded tw-z-50 tw-shadow-lg"
        style={{
          top: idTooltip.position.top + 'px',
          left: idTooltip.position.left + 'px',
          transform: 'translateX(-50%)',
        }}
      >
        {idTooltip.id}
      </div>
    );
  }
  return null;
}

// Function to format ID to show only last 4 characters
const formatId = (id: string) => {
  if (!id) return '';
  return id.length > 4 ? `...${id.slice(-4)}` : id;
};

function ArtistDiscographySourceIdCellImplementation({
  artist,
  source,
  expanded = true,
  openIdDetailsModal,
  handleAddIdFromCell,
}: {
  readonly artist: DiscographyId;
  readonly source: keyof DiscographyId['source_ids'];
  readonly expanded?: boolean;
  readonly handleAddIdFromCell: (
    artistId: string,
    platform: keyof DiscographyId['source_ids']
  ) => void;
  readonly openIdDetailsModal: (
    artistId: string,
    platform: keyof DiscographyId['source_ids']
  ) => void;
}) {
  const sourceIds = artist.source_ids[source];

  // Function to format ID to show only last 4 characters
  const [idTooltip, setIdTooltip] = useState<IdTooltip | null>(null);

  const [displayText, setDisplayText] = useState(
    sourceIds.length > 0
      ? sourceIds.some((id) => !id.valid && !id.approved && !id.rejected)
        ? sourceIds.find((id) => !id.valid && !id.approved && !id.rejected)?.id
        : sourceIds[0].id
      : ''
  );
  const [isMeasuring, setIsMeasuring] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  // Function to show full ID on hover
  const showFullId = useCallback((id: string, event: React.MouseEvent) => {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    setIdTooltip({
      id,
      position: {
        top: rect.top - 30,
        left: rect.left + rect.width,
      },
    });
  }, []);

  // Function to hide full ID
  const hideFullId = useCallback(() => {
    setIdTooltip(null);
  }, []);

  useEffect(() => {
    setIsMeasuring(true);
  }, [artist]);

  useLayoutEffect(() => {
    if (sourceIds.length === 0) {
      setIsMeasuring(false);
      return;
    }

    if (containerRef.current) {
      const element = containerRef.current;
      // We render with the full ID initially. If the container's height is larger
      // than a single line's height, it means the content wrapped.
      const firstSpan = element.querySelector('span');
      if (firstSpan) {
        const singleLineHeight = firstSpan.offsetHeight;
        if (element.offsetHeight > singleLineHeight * 1.75) {
          setDisplayText(formatId(sourceIds[0].id));
          setIsMeasuring(false);
          return;
        }
      }
    }

    if (displayText?.length === 0) {
      // This is in case we don't need to collapse the id
      setDisplayText(
        sourceIds.length > 0
          ? sourceIds.some((id) => !id.valid && !id.approved && !id.rejected)
            ? sourceIds.find((id) => !id.valid && !id.approved && !id.rejected)
                ?.id
            : sourceIds[0].id
          : ''
      );
    }
    setIsMeasuring(false);
  }, [sourceIds, isMeasuring]);

  useEffect(() => {
    console.log('mounted', { id: artist.id, source });
    return () => {
      console.log('un-mounted', { id: artist.id, source });
    };
  }, []);

  if (sourceIds.length > 0) {
    if (expanded) {
      return (
        <div className="tw-flex tw-flex-col gap-2 tw-max-w-full tw-min-w-full">
          {sourceIds.map((sourceId) => (
            <div
              className="tw-flex tw-flex-col tw-flex-wrap tw-items-start tw-text-xs"
              key={sourceId.id}
            >
              <SourceIdResolver
                artist={artist}
                source={source}
                source_id={sourceId.id}
              />
            </div>
          ))}
        </div>
      );
    }

    const someInReview = sourceIds.some(
      (id) => !id.valid && !id.approved && !id.rejected
    );
    const firstInReview = sourceIds.find(
      (id) => !id.valid && !id.approved && !id.rejected
    );
    const sourceId = someInReview ? firstInReview : sourceIds[0];

    if (!sourceId) return null;

    const isApproved = sourceId.approved;
    const isRejected = sourceId.rejected;
    const isValid = sourceId.valid && !isRejected && !isApproved;
    const isUnderReview = !isValid && !isRejected && !isApproved;

    return (
      <div
        className={`tw-cursor-pointer`}
        onClick={() => openIdDetailsModal(artist.id, source)}
      >
        <div className="tw-flex tw-flex-col">
          <div
            ref={containerRef}
            className="tw-flex tw-flex-wrap tw-items-center tw-text-xs"
            style={{
              visibility: isMeasuring ? 'hidden' : 'visible',
            }}
          >
            <span className="tw-text-gray-400 tw-mr-1">
              {sourceId?.original_name}
            </span>
            <span className="tw-flex tw-flex-wrap tw-items-center">
              <span
                className={[
                  isApproved ? 'tw-text-westburyGreen' : '',
                  isRejected ? 'tw-text-westburyRed' : '',
                  isValid ? 'tw-text-westburyBlue' : '',
                  isUnderReview ? 'tw-text-westburyAmber' : '',
                ].join(' ')}
                onMouseEnter={(e) => sourceId && showFullId(sourceId.id, e)}
                onMouseLeave={hideFullId}
              >
                {displayText}
              </span>
              {sourceIds.length > 1 && (
                <span
                  className={[
                    'tw-ml-2  tw-text-gray-300 tw-text-xs tw-px-1.5 tw-py-0.5 tw-rounded-full',
                    someInReview ? 'tw-bg-westburyAmber' : 'tw-bg-gray-700',
                  ].join(' ')}
                >
                  +{sourceIds.length - 1}
                </span>
              )}
            </span>
          </div>
        </div>
        <Tooltip idTooltip={idTooltip} />
      </div>
    );
  }

  return (
    <button
      className="tw-inline-flex tw-items-center tw-justify-center tw-text-surfaceLime tw-text-xs tw-border tw-border-gray-700 tw-rounded-md hover:tw-bg-gray-800"
      onClick={() => handleAddIdFromCell(artist.id, source)}
    >
      <Plus size={10} className="tw-mr-1" />
      Add ID
    </button>
  );
}

function ArtistDiscographySourceIdCellWrapper(
  props: ComponentProps<typeof ArtistDiscographySourceIdCellImplementation>
) {
  console.log('rerendering cell ArtistDiscographySourceIdCellWrapper');
  return (
    <div className="tw-py-2 tw-px-3 tw-flex tw-justify-start tw-items-start tw-align-top tw-border-r tw-border-gray-700 tw-w-1/5">
      <ArtistDiscographySourceIdCellImplementation {...props} />
    </div>
  );
}

const ArtistDiscographySourceIdCell = memo(
  ArtistDiscographySourceIdCellWrapper,
  (prevProps, nextProps) => {
    return Boolean(
      prevProps.artist.id === nextProps.artist.id &&
        prevProps.source === nextProps.source &&
        prevProps.openIdDetailsModal === nextProps.openIdDetailsModal
    );
  }
);

export { ArtistDiscographySourceIdCell };
